import { DatePicker } from 'antd';
import moment from 'moment-timezone';
import React, { memo } from 'react';
import { useDispatch } from 'react-redux';

import Field from '@fmbt/ui/Field';
import InlineHelp from 'components/composites/InlineHelp';
import { EM_DASH } from 'constants/index';
import { useMyUserHasInternalPermission } from 'store/modules/myUser/hooks';
import { editSamplePoint } from 'store/modules/samplePoints/actions';
import { ProtectedResource, ResourceAction } from 'types/action.enum';
import SamplePoint from 'types/models/samplePoint';

import { STANDARD_PHOTO_SIZE_KB } from '../helpers';

interface Props {
  samplePoint: SamplePoint;
  isBackOffice?: boolean;
}

function CameraDetails({ samplePoint, isBackOffice = false }: Props) {
  const dispatch = useDispatch();
  const hasPermissionToUpdatePlanResetDate = useMyUserHasInternalPermission({
    resource: ProtectedResource.SAMPLE_POINT,
    action: ResourceAction.UPDATE
  });
  const showPlanResetDatePicker = isBackOffice && hasPermissionToUpdatePlanResetDate;

  if (
    !samplePoint.config ||
    samplePoint.config.maxKb === undefined ||
    samplePoint.config.kbConsumed === undefined ||
    samplePoint.config.resetDate === undefined ||
    samplePoint.site?.timezoneCode === undefined
  ) {
    return null;
  }

  const {
    config: { maxKb, kbConsumed, resetDate },
    site: { timezoneCode: siteTimezoneCode }
  } = samplePoint;

  const updatePlanResetDate = (newDate: moment.Moment | null) =>
    newDate &&
    dispatch(
      editSamplePoint(samplePoint, {
        config: {
          ...samplePoint.config,
          resetDate: newDate.format('YYYY-MM-DD')
        }
      })
    );

  const date = resetDate ? moment.tz(resetDate, siteTimezoneCode) : null;

  return (
    <div>
      <div className="body-copy">
        Standard photos left:{' '}
        {Math.floor((maxKb - kbConsumed) / STANDARD_PHOTO_SIZE_KB)}/
        {Math.floor(maxKb / STANDARD_PHOTO_SIZE_KB)}
        <InlineHelp textContent={`Based on a Standard Quality photo size of ${STANDARD_PHOTO_SIZE_KB} KB.`} />
      </div>
      <div className="body-copy">
        Data left: {maxKb - kbConsumed} KB
      </div>
      {showPlanResetDatePicker ? (
        <div className="mt-8">
          <Field label="Plan resets">
            <DatePicker
              style={{ width: '100%' }}
              allowClear={false}
              value={date}
              onChange={updatePlanResetDate}
            />
          </Field>
        </div>
      ) : (
        <div className="body-copy">Plan resets: {date ? date.format('Do MMM YYYY') : EM_DASH}</div>
      )}
    </div>
  );
}

export default memo(CameraDetails);
